import { FontWeights, getTheme, mergeStyleSets } from '@fluentui/react';

export const useAdminStyles = () => {
    const theme = getTheme();

    const contentStyles = mergeStyleSets({
        mainContainer: {
            minHeight: '100vh',
            background: `linear-gradient(135deg, ${theme.palette.themeLighter} 0%, ${theme.palette.white} 100%)`,
            padding: '0',
            margin: '0',
        },
        headerSection: {
            position: 'sticky' as const,
            top: 0,
            zIndex: 1000,
            background: theme.palette.white,
            boxShadow: theme.effects.elevation4,
            padding: '12px 40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '40px',
            selectors: {
                '@media (max-width: 480px)': {
                    padding: '10px 16px',
                },
            },
        },
        logoSection: {
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
        },
        logo: {
            height: '60px',
            width: 'auto',
            cursor: 'pointer',
            transition: 'opacity 0.2s ease',
            ':hover': {
                opacity: 0.8,
            },
        },
        title: {
            fontSize: '32px',
            fontWeight: FontWeights.bold,
            color: theme.palette.themeDark,
            margin: '0',
            letterSpacing: '-0.5px',
        },
        subtitle: {
            fontSize: '16px',
            color: theme.palette.neutralSecondary,
            margin: '0 0 0 8px',
            fontWeight: FontWeights.regular,
        },
        userSection: {
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '12px 20px',
            background: theme.palette.neutralLighterAlt,
            borderRadius: '8px',
            border: `1px solid ${theme.palette.neutralQuaternaryAlt}`,
        },
        userName: {
            fontWeight: FontWeights.semibold,
            color: theme.palette.neutralDark,
        },
        contentArea: {
            maxWidth: '95%',
            margin: '0 auto',
            padding: '0 20px',
        },
        adminCard: {
            background: theme.palette.white,
            borderRadius: '12px',
            padding: '32px',
            boxShadow: theme.effects.elevation8,
            marginBottom: '32px',
        },
        adminTitle: {
            fontSize: '24px',
            fontWeight: FontWeights.bold,
            color: theme.palette.themeDark,
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
        },
        adminDescription: {
            fontSize: '14px',
            color: theme.palette.neutralSecondary,
            marginBottom: '24px',
        },
        actionBar: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
            padding: '16px 24px',
            background: theme.palette.neutralLighterAlt,
            borderRadius: '8px',
            border: `1px solid ${theme.palette.neutralQuaternaryAlt}`,
        },
        viewToggleContainer: {
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '12px 16px',
            background: theme.palette.white,
            borderRadius: '8px',
            border: `1px solid ${theme.palette.neutralQuaternaryAlt}`,
            marginBottom: '16px',
        },
        viewToggleLabel: {
            fontSize: '14px',
            fontWeight: FontWeights.semibold,
            color: theme.palette.neutralDark,
        },
        statsContainer: {
            display: 'flex',
            gap: '24px',
            alignItems: 'center',
        },
        statItem: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
        },
        statNumber: {
            fontSize: '20px',
            fontWeight: FontWeights.bold,
            color: theme.palette.themePrimary,
        },
        statLabel: {
            fontSize: '12px',
            color: theme.palette.neutralSecondary,
            textAlign: 'center',
        },
        backButton: {
            minWidth: '120px',
            height: '36px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: FontWeights.semibold,
        },
        buttonContainer: {
            display: 'flex',
            gap: '12px',
        },
        gridContainer: {
            background: theme.palette.white,
            borderRadius: '12px',
            padding: '24px',
            boxShadow: theme.effects.elevation4,
            overflow: 'hidden',
            width: '100%',
            maxWidth: '100%',
        },
        gridHeader: {
            fontSize: '18px',
            fontWeight: FontWeights.bold,
            color: theme.palette.themeDark,
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
        },
        overlay: {
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
            top: '0',
            bottom: '0',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            left: '0',
            verticalAlign: 'middle',
            position: 'fixed',
            right: '0',
            margin: 0,
            textAlign: 'center',
            zIndex: 1000010,
        },
        spinnerContainer: {
            background: theme.palette.white,
            borderRadius: '12px',
            padding: '40px',
            boxShadow: theme.effects.elevation16,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
        },
        loginCard: {
            background: theme.palette.white,
            borderRadius: '12px',
            padding: '60px 40px',
            boxShadow: theme.effects.elevation8,
            textAlign: 'center',
            maxWidth: '400px',
            margin: '0 auto',
        },
        loginTitle: {
            fontSize: '24px',
            fontWeight: FontWeights.bold,
            color: theme.palette.themeDark,
            marginBottom: '16px',
        },
        loginSubtitle: {
            fontSize: '14px',
            color: theme.palette.neutralSecondary,
            marginBottom: '32px',
        },
        primaryActionButton: {
            minWidth: '180px',
            height: '48px',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: FontWeights.semibold,
        },
        iconBadge: {
            background: `linear-gradient(135deg, ${theme.palette.themePrimary}, ${theme.palette.themeSecondary})`,
            color: 'white',
            padding: '8px',
            borderRadius: '6px',
            fontSize: '16px',
        },
    });

    return { theme, contentStyles };
};
